import * as React from 'react';

import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { ConsoleDialog } from '@rematter/ui-web';
import { FontLight, FormattedAddress, LocalizedDate } from '@rematter/ui-web';
import { ContentInfo, ContentSection } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideCalendar, LucideDot } from 'lucide-react';

import * as state from '../../_state/purchaseOrderDetails.state';
import { customerPortalRoutes } from '../../routes';
import { FileRecord } from '../../types';
import { FileCard } from '../_shared/FileCard';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const CounterpartySalesOrderDialog = () => {
  const [, navigate] = useModuleLocation();
  const purchaseOrderId = useAtomValue(state.$purchaseOrderId);
  const { data: purchaseOrder } = useAtomValue(state.$purchaseOrderDetails);

  const recyclerFiles = React.useMemo(
    () => purchaseOrder?.recyclerFiles ?? [],
    [purchaseOrder?.recyclerFiles],
  );

  return (
    <ConsoleDialog
      title={
        <div className="flex items-center gap-1">
          Counterparty Sales Order <LucideDot size={16} />
          <FontLight>PO #{purchaseOrder?.purchaseOrderNumber}</FontLight>
        </div>
      }
      content={
        <div className="w-[1024px]">
          <ContentSection title="Contract">
            <div className={'grid grid-cols-3 gap-4'}>
              <ContentInfo
                label="Sales Order #"
                value={purchaseOrder?.salesOrderNumber}
                children={(soNumber) => soNumber}
              />
              <ContentInfo
                label="Start Date"
                value={purchaseOrder?.startDate}
                children={(startDate) => (
                  <div className={'flex items-center gap-1.5'}>
                    <LucideCalendar size={16} className={'opacity-30'} />
                    <LocalizedDate
                      date={maybeParseCalendarDate(startDate)}
                      dateStyle="medium"
                      showRelativeDate
                    />
                  </div>
                )}
              />
              <ContentInfo
                label="End Date"
                value={purchaseOrder?.endDate}
                children={(endDate) => (
                  <div className={'flex items-center gap-1.5'}>
                    <LucideCalendar size={16} className={'opacity-30'} />
                    <LocalizedDate
                      date={maybeParseCalendarDate(endDate)}
                      dateStyle="medium"
                      showRelativeDate
                    />
                  </div>
                )}
              />
              <ContentInfo
                label="Account Representative"
                value={purchaseOrder?.accountRepresentative}
                children={(accountRepresentative) =>
                  [
                    accountRepresentative?.firstName,
                    accountRepresentative?.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
              />
              <ContentInfo
                label="Counterparty PO #"
                value={purchaseOrder?.purchaseOrderNumber}
              >
                {(counterpartyPoNumber) => counterpartyPoNumber}
              </ContentInfo>
            </div>
          </ContentSection>
          <ContentSection title="Supplier">
            <div className={'grid grid-cols-3 gap-4'}>
              <ContentInfo
                label="Supplier Name"
                value={purchaseOrder?.supplier?.name}
              >
                {(supplierName) => supplierName}
              </ContentInfo>
              <ContentInfo
                label="Location"
                value={purchaseOrder?.supplier?.addresses?.[0]}
              >
                {(address) => <FormattedAddress {...address} />}
              </ContentInfo>
              <ContentInfo label="Contact" value={purchaseOrder?.contact}>
                {(contact) => (
                  <div className={'flex flex-col gap-1'}>
                    <span>{contact?.fullName}</span>
                    {contact?.email && (
                      <a
                        className={'text-xs text-neutral/60'}
                        href={`mailto:${contact?.email}`}
                      >
                        {contact?.email}
                      </a>
                    )}
                    {contact?.phone && (
                      <a
                        className={'text-xs text-neutral/60'}
                        href={`tel:${contact?.phone}`}
                      >
                        {contact?.phone}
                      </a>
                    )}
                  </div>
                )}
              </ContentInfo>
            </div>
          </ContentSection>
          <ContentSection title="Payment">
            <div className={'grid grid-cols-3 gap-4'}>
              <ContentInfo
                label="Currency"
                value={purchaseOrder?.paymentCurrency}
              >
                {(paymentMethod) => paymentMethod}
              </ContentInfo>
              <ContentInfo
                label="Payment Term"
                value={purchaseOrder?.paymentTerm}
              >
                {(paymentTerm) => paymentTerm}
              </ContentInfo>
              <ContentInfo
                label="Freight Term"
                value={purchaseOrder?.freightTerm}
              >
                {(freightTerm) => freightTerm}
              </ContentInfo>
            </div>
          </ContentSection>
          <ContentSection title="Notes">
            <p className={'max-w-[600px] text-sm text-neutral/60'}>
              {purchaseOrder?.recyclerExternalNotes}
            </p>
          </ContentSection>
          <ContentSection title="Attachments">
            {!!recyclerFiles.length && (
              <div className="grid grid-cols-3 gap-3">
                {recyclerFiles.map((file: FileRecord) => (
                  <FileCard key={file.id} file={file} />
                ))}
              </div>
            )}
            {!recyclerFiles.length && (
              <div className="flex text-sm text-neutral/40">None</div>
            )}
          </ContentSection>
        </div>
      }
      isOpen
      onOpenChange={() => {
        navigate(customerPortalRoutes.purchaseOrderDetails, {
          params: {
            purchaseOrderId,
          },
        });
      }}
      className="@CounterpartySalesOrderDialog"
    />
  );
};
